import { PostMessageSender, PostMessageHandlers, PostMessageReply } from '../Shared/PostMessage';
import {
  FocusTitleMessage,
  FocusTitleMessageId,
  SetTitleTextMessage,
  SetTitleReadonlyModeMessage,
  SetTitleTextMessageId,
  SetTitleReadonlyModeMessageId,
  NotifyComponentTitleChangeMessage,
  NotifyComponentTitleChangeMessageId,
  GetTitleTextMessageId,
  GetTitleTextMessage,
  GetTitleTextReplyMessage
} from '../MessageTypes/ComponentTitleHandlerMessageTypes';
import { ComponentTitleHandler } from '@fluidx/office-fluid-types';
import { IFluidObject } from '@fluidframework/core-interfaces';
import { EventEmitter } from 'events';

/**
 * Initializes everything needed to handle the ComponentTitleHandler interface outside the iframe
 * @param sender how to send messages to the inside of the iframe
 * @returns A dummy component with just this interface and the set of message handlers this interface is expecting
 */
export function getHostSideComponentTitleHandler(
  sender: PostMessageSender
): {
  component: ComponentTitleHandler;
  handlers: PostMessageHandlers<string>;
  dispose: () => void;
} {
  const component = new HostSideComponentTitleHandlerProxy(sender);

  const handlers: PostMessageHandlers<string> = {
    [NotifyComponentTitleChangeMessageId]: (message) => {
      const titleUpdate: string = (message as NotifyComponentTitleChangeMessage).payload.titleText;
      component.emit('titleChange', titleUpdate);
    }
  };
  return {
    component,
    handlers,
    dispose: () => component.removeAllListeners()
  };
}

class HostSideComponentTitleHandlerProxy extends EventEmitter implements ComponentTitleHandler {
  constructor(private sender: PostMessageSender) {
    super();
  }

  get ComponentTitleHandler(): ComponentTitleHandler {
    return this;
  }

  setTitleText(titleText: string): Promise<void> {
    const message: SetTitleTextMessage = {
      type: SetTitleTextMessageId,
      payload: { titleText }
    };
    return this.sender.sendWithReturn(message).then((response: PostMessageReply) => {
      if (response.error) {
        throw new Error('Could not set title text');
      }
    });
  }

  setTitleReadonlyMode(isReadonlyMode: boolean): Promise<void> {
    const message: SetTitleReadonlyModeMessage = {
      type: SetTitleReadonlyModeMessageId,
      payload: { isReadonlyMode }
    };
    return this.sender.sendWithReturn(message).then((response: PostMessageReply) => {
      if (response.error) {
        throw new Error('Could not set title as readonly');
      }
    });
  }

  focusTitle(): Promise<void> {
    const message: FocusTitleMessage = {
      type: FocusTitleMessageId
    };
    return this.sender.sendWithReturn(message).then((response: PostMessageReply) => {
      if (response.error) {
        throw new Error('Could not focus title');
      }
    });
  }

  getTitleText(): Promise<string | undefined> {
    const message: GetTitleTextMessage = {
      type: GetTitleTextMessageId
    };
    return this.sender.sendWithReturn(message).then((response: PostMessageReply) => {
      if (response.error) {
        throw new Error('Could not get title text');
      }
      return (response as GetTitleTextReplyMessage).payload;
    });
  }

  dispose() {
    this.removeAllListeners();
  }
}

/**
 * Initializes everything needed to handle the ComponentTitleHandler interface inside the iframe
 * @param component the actual component with the real implementation of the interface
 * @param sender how to send messages to the outside of the iframe
 */
export function getGuestSideComponentTitleHandler(
  component: IFluidObject,
  sender: PostMessageSender
): PostMessageHandlers<string> | undefined {
  // TODO: (Bug #4477322: Temp hack for OneNote live-site issue)
  const componentTitleHandler =
    component.ComponentTitleHandler ??
    ((component as { viewComponent: IFluidObject }).viewComponent as ComponentTitleHandler);

  if (!componentTitleHandler) {
    return undefined;
  }

  componentTitleHandler.on('titleChange', (updatedTitle) => {
    // Send a message to the host to notify that the titleChange event has been emitted
    const updateMessage: NotifyComponentTitleChangeMessage = {
      type: NotifyComponentTitleChangeMessageId,
      payload: { titleText: updatedTitle }
    };
    sender.send(updateMessage);
  });

  return {
    [SetTitleTextMessageId]: (message) => {
      componentTitleHandler
        .setTitleText((message as SetTitleTextMessage).payload.titleText)
        .then(() => {
          sender.sendReply(message, {});
        })
        .catch(() => {
          sender.sendReply(message, { error: true });
        });
    },
    [SetTitleReadonlyModeMessageId]: (message) => {
      componentTitleHandler
        .setTitleReadonlyMode((message as SetTitleReadonlyModeMessage).payload.isReadonlyMode)
        .then(() => {
          sender.sendReply(message, {});
        })
        .catch(() => {
          sender.sendReply(message, { error: true });
        });
    },
    [FocusTitleMessageId]: (message) => {
      componentTitleHandler
        .focusTitle()
        .then(() => {
          sender.sendReply(message, {});
        })
        .catch(() => {
          sender.sendReply(message, { error: true });
        });
    },
    [GetTitleTextMessageId]: (message) => {
      componentTitleHandler
        .getTitleText()
        .then((result) => {
          sender.sendReply(message, { payload: result });
        })
        .catch(() => {
          sender.sendReply(message, { error: true });
        });
    }
  };
}
